// types
import { Loan } from '../../../@types';
import { financiersExt } from '../../../@types/unions';

// i18next
import { useTranslation } from 'react-i18next';

// material
import { Grid } from '@mui/material';

// cards
import { CardKeyValue } from '../../cards/CardKeyValue/CardKeyValue';

// utils
import { formatNumber } from '../../../utils/format';
import { formatCurrency } from '../../../utils/currency';

// props
export interface SummaryFinancingSourcesProps {
	currency?: null | string;
	loans: Loan[];
}

export const SummaryFinancingSources = ({ currency, loans }: SummaryFinancingSourcesProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	let amount = 0;
	let disbursed = 0;

	// check if all loans are external financing
	const isAllExtFinancing = loans.every((loan) => financiersExt.some((f) => loan.financier === f));

	for (let i = 0; i < loans.length; i++) {
		amount += loans[i].amount;
		disbursed += loans[i].disbursed;
	}

	let undisbursed = amount - disbursed;

	let amountFormatted = formatNumber(amount);
	let disbursedFormatted = formatNumber(disbursed);
	let undisbursedFormatted = isAllExtFinancing ? t('na') : formatNumber(undisbursed);

	if (currency) {
		amount = 0;
		disbursed = 0;

		for (let i = 0; i < loans.length; i++) {
			const conversionsAmountByCurrency = loans[i].conversions.filter(
				(c) => c.type === 'AMOUNT' && c.currency === currency
			);
			const conversionsDisbursedByCurrency = loans[i].conversions.filter(
				(c) => c.type === 'DISBURSED' && c.currency === currency
			);

			// sum original amount and disbursed if in selected currency
			if (loans[i].currency === currency) {
				amount += loans[i].amount;
				disbursed += loans[i].disbursed || 0;
			}

			// sum amounts in selected currency
			for (let a = 0; a < conversionsAmountByCurrency.length; a++) {
				amount += conversionsAmountByCurrency[a].amount;
			}

			// sum disbursed in selected currency
			for (let a = 0; a < conversionsDisbursedByCurrency.length; a++) {
				disbursed += conversionsDisbursedByCurrency[a].amount || 0;
			}
		}

		// redefine undisbursed
		undisbursed = amount - disbursed;

		// reformat values
		amountFormatted = formatCurrency({
			currency,
			value: amount,
		});

		disbursedFormatted = formatCurrency({
			currency,
			value: disbursed,
		});

		undisbursedFormatted = formatCurrency({
			currency,
			value: undisbursed,
		});

		if (isAllExtFinancing) {
			undisbursedFormatted = t('na');
		}
	}

	let percent = 0;

	if (typeof amount === 'number' && typeof disbursed === 'number') {
		if (amount > 0) {
			percent = Math.round((disbursed / amount) * 100);
		}
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={6} lg={3}>
				<CardKeyValue
					label={{ children: t('totalAmount') }}
					value={{ children: amountFormatted }}
				/>
			</Grid>
			<Grid item xs={6} lg={3}>
				<CardKeyValue
					label={{ children: t('undisbursed') }}
					value={{ children: undisbursedFormatted }}
				/>
			</Grid>
			<Grid item xs={12} lg={6}>
				<CardKeyValue
					label={{ children: t('disbursed') }}
					percent={percent}
					value={{ children: disbursedFormatted }}
				/>
			</Grid>
		</Grid>
	);
};
